import { FAPI_BASE } from './fapiBase';
import { SITE_TRADING_ENABLED, TRADING_MOVED_MESSAGE } from '../config';
import { api } from '../api/client';
import { STORED } from './accountKey';
import { tr } from '@quantum/ui';
export { FAPI_BASE };

export async function getBinancePrecision(symbol) {
  if (!window.cyberExchangeInfo) {
    try {
      const res = await fetch(`${FAPI_BASE}/fapi/v1/exchangeInfo`);
      window.cyberExchangeInfo = await res.json();
    } catch (e) {
      console.error('Failed to fetch exchangeInfo', e);
      return null;
    }
  }
  const symInfo = window.cyberExchangeInfo?.symbols?.find(s => s.symbol === symbol);
  if (!symInfo) return null;
  
  const priceFilter = symInfo.filters?.find(f => f.filterType === 'PRICE_FILTER');
  const lotFilter = symInfo.filters?.find(f => f.filterType === 'LOT_SIZE');
  
  return {
    pricePrec: symInfo.pricePrecision || 5,
    qtyPrec: symInfo.quantityPrecision || 3,
    tickSize: priceFilter ? parseFloat(priceFilter.tickSize) : null,
    stepSize: lotFilter ? parseFloat(lotFilter.stepSize) : null
  };
}

export function strictRound(value, stepSize) {
  if (!stepSize) return value;
  const rounded = Math.round(value / stepSize) * stepSize;
  // Format based on decimals in stepSize to avoid floating point errors (e.g. 0.000000001)
  const stepStr = stepSize.toString();
  const decimals = stepStr.includes('.') ? stepStr.split('.')[1].length : 0;
  return Number(rounded.toFixed(decimals));
}


export async function signBinanceRequest(queryString, secret) {
  const enc = new TextEncoder();
  const key = await window.crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await window.crypto.subtle.sign('HMAC', key, enc.encode(queryString));
  return Array.from(new Uint8Array(signature)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function binanceSignedFetch(endpoint, method, params, apiKey, apiSecret) {
  // Merge decision: no exchange writes from the web app — trading is the
  // desktop terminal's job. Reads (GET: balance, positionRisk, leverage
  // brackets) stay allowed so the position calculator keeps working.
  if (!SITE_TRADING_ENABLED && String(method).toUpperCase() !== 'GET') {
    throw new Error(TRADING_MOVED_MESSAGE);
  }

  // The account key stored on OUR server (Account → Exchange API): signed
  // there, sent from its IP — the key never reaches the browser.
  if (apiKey === STORED) return storedFetch(endpoint, method, params);

  // Trim keys defensively — prevents silent failures from copy-pasted keys with spaces
  const cleanKey    = (apiKey    || '').trim();
  const cleanSecret = (apiSecret || '').trim();

  if (!cleanKey || !cleanSecret) {
    throw new Error(tr('API key or Secret not set. Check settings.'));
  }

  // DIRECT from the browser → the request originates from the USER'S OWN IP. The
  // secret never leaves the device, load is spread across all users, and there's
  // no shared server IP to allow-list. The key just needs Futures enabled and
  // either no IP restriction (Unrestricted) or the user's own IP allow-listed.
  params.timestamp = Date.now();
  const queryString = Object.keys(params)
    .sort()
    .map(k => `${k}=${params[k]}`)
    .join('&');
  const signature  = await signBinanceRequest(queryString, cleanSecret);
  const url        = `${FAPI_BASE}${endpoint}?${queryString}&signature=${signature}`;

  const res = await fetch(url, { method, headers: { 'X-MBX-APIKEY': cleanKey } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // Enriched error message for the most common Binance auth errors
    if (data.code === -2015 || data.code === -2014) {
      throw new Error(
        `Binance: ${data.msg}\n\n` +
        tr("Requests are sent straight from YOUR device, so no special IP setup is needed.\nOn Binance → API Management → your key:\n\n1. Enable \"Enable Futures\" (not only Reading).\n2. EASIEST: set \"IP access restrictions\" to \"Unrestricted (less secure)\" — then it works from any network. (Or restrict to IPs and add YOUR current IP — note Binance may see IPv6 while you added IPv4, so add both or use Unrestricted.)\n3. Key from the main account (not a sub-account/testnet); Secret has no extra spaces.\n4. After editing the key, wait ~30s and retry.")
      );
    }
    throw new Error(data.msg || `Binance Error ${data.code}`);
  }
  return data;
}

async function storedFetch(endpoint, method, params) {
  try {
    return await api('/binance/stored', { method: 'POST', body: { endpoint, method, params: params || {} } });
  } catch (e) {
    const d = e.payload || {};
    if (d.code === -2015 || d.code === -2014) {
      throw new Error(`Binance: ${d.msg}\n\n` + tr('Add {ip} to this key’s trusted IPs on Binance (API Management → your key → Restrict access to trusted IPs), with Futures enabled.', { ip: d.serverIp || '94.130.225.155' }), { cause: e });
    }
    throw new Error(d.msg || e.message, { cause: e });
  }
}
